import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, FileDown, Link2, Loader2, Layers, List, Mail } from "lucide-react";
import { API, authHeaders } from "../context/AuthContext";
import { useLang } from "../i18n/LangContext";
import { StandardReportContent } from "../components/StandardReportContent";
import { PremiumReportContent } from "../components/PremiumReportContent";
import { ReportDeckView } from "../components/ReportDeckView";
import { EmailShareDialog } from "../components/EmailShareDialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Report() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [view, setView] = useState("deck"); // 'deck' | 'list'
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await axios.get(`${API}/reports/${id}`, { headers: authHeaders() });
        if (!cancelled) setDoc(r.data);
      } catch {
        toast.error("Not found");
        navigate("/history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await axios.get(`${API}/reports/${id}/pdf`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dating-coach-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF failed");
    } finally {
      setPdfLoading(false);
    }
  };

  const createShare = async () => {
    setShareLoading(true);
    try {
      const res = await axios.post(`${API}/reports/${id}/share`, {}, { headers: authHeaders() });
      const origin = window.location.origin;
      const link = `${origin}/share/${res.data.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success(t("shareCopied"));
    } catch {
      toast.error("Share failed");
    } finally {
      setShareLoading(false);
    }
  };

  if (loading || !doc) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#ff4458] border-t-transparent" />
      </div>
    );
  }

  const isPremium = doc.tier === "premium";

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="report-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => navigate("/dashboard")} data-testid="report-back" className="inline-flex items-center gap-2 text-sm text-[#a8adbd] hover:text-white">
          <ArrowLeft className="h-4 w-4" />{t("backToDashboard")}
        </button>

        <div className="flex flex-wrap items-center gap-2">
          {/* View toggle */}
          <div
            className="inline-flex items-center rounded-full border border-[#222636] bg-[#181b24] p-1"
            data-testid="view-toggle"
          >
            <button
              type="button"
              onClick={() => setView("deck")}
              data-testid="view-toggle-deck"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                view === "deck"
                  ? "dc-gradient-bg text-white shadow-[0_6px_16px_rgba(253,38,122,0.35)]"
                  : "text-[#a8adbd] hover:text-white"
              }`}
            >
              <Layers className="h-3.5 w-3.5" /> Deck
            </button>
            <button
              type="button"
              onClick={() => setView("list")}
              data-testid="view-toggle-list"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                view === "list"
                  ? "dc-gradient-bg text-white shadow-[0_6px_16px_rgba(253,38,122,0.35)]"
                  : "text-[#a8adbd] hover:text-white"
              }`}
            >
              <List className="h-3.5 w-3.5" /> List
            </button>
          </div>

          <button onClick={downloadPdf} disabled={pdfLoading} data-testid="download-pdf-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#222636] bg-[#181b24] px-4 py-2 text-sm font-semibold text-white hover:border-[#ff4458]/40">
            {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            {t("downloadPdf")}
          </button>
          <button
            type="button"
            onClick={() => setEmailDialogOpen(true)}
            data-testid="email-share-btn"
            className="inline-flex items-center gap-2 rounded-full border border-[#222636] bg-[#181b24] px-4 py-2 text-sm font-semibold text-white hover:border-[#ff4458]/40"
          >
            <Mail className="h-4 w-4" />
            Email
          </button>
          <button onClick={createShare} disabled={shareLoading} data-testid="share-btn"
            className="inline-flex items-center gap-2 rounded-full dc-gradient-bg px-4 py-2 text-sm font-semibold text-white shadow-[0_6px_16px_rgba(253,38,122,0.3)]">
            {shareLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {t("share")}
          </button>
        </div>
      </div>

      <EmailShareDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        reportId={id}
      />

      {view === "deck" ? (
        <ReportDeckView doc={doc} tier={isPremium ? "premium" : "standard"} />
      ) : (
        <div className="dc-force-dark">
          {isPremium ? <PremiumReportContent doc={doc} /> : <StandardReportContent doc={doc} />}
        </div>
      )}
    </div>
  );
}
